import { useEffect, useRef } from "react"
import type { MatterFile } from "../contract"

/** What a reader is told about a file Nira holds. Only the backend's own `status` decides it. */
export type FilePhase = "reading" | "ready" | "failed" | "added"
export interface FileState { phase: FilePhase; label: string; detail?: string }

const READING = new Set(["queued", "rendering", "indexing", "summarising"])

const pages = (n: number): string => `${n} ${n === 1 ? "page" : "pages"}`

/**
 * queued/rendering/indexing/summarising -> Reading (with pages done of total once the page count is known);
 * ready -> Read; failed -> Could not be read. A missing or unrecognised status is just "Added": never "Reading".
 */
export function fileState(f: Pick<MatterFile, "status" | "pages_total" | "pages_done" | "error">): FileState {
  const s = f.status as string | undefined
  if (s === "ready") return { phase: "ready", label: f.pages_total > 0 ? `Read · ${pages(f.pages_total)}` : "Read" }
  if (s === "failed") return { phase: "failed", label: "Could not be read", detail: f.error || undefined }
  if (s && READING.has(s)) {
    const known = f.pages_total > 0
    return { phase: "reading", label: known ? `Reading · ${Math.min(f.pages_done, f.pages_total)} of ${f.pages_total} pages` : "Reading…" }
  }
  return { phase: "added", label: "Added" }
}

/** True while the backend says the file is still being worked on. */
export const isProcessing = (f: Pick<MatterFile, "status">): boolean => fileState({ status: f.status, pages_total: 0, pages_done: 0 }).phase === "reading"

export const POLL_FIRST_MS = 2000
export const POLL_MAX_MS = 15000
export const POLL_GIVE_UP_MS = 20 * 60 * 1000

/**
 * Calls `refresh` with growing gaps (2s, 3s, 4.5s ... up to 15s) while any file is processing.
 * Stops once every file is terminal, or after 20 minutes; the timer is cleared on unmount.
 * A change in the file list (new upload, progress) restarts the gaps from the start.
 */
export function useProcessingPoll(files: Pick<MatterFile, "status" | "pages_done">[], refresh: () => void): void {
  const latest = useRef(refresh)
  latest.current = refresh
  const processing = files.some(isProcessing)
  // Progress or a new file means the backend is moving: poll quickly again.
  const signature = files.map((f) => `${f.status}:${f.pages_done}`).join("|")
  useEffect(() => {
    if (!processing) return
    let timer: ReturnType<typeof setTimeout> | undefined
    let gap = POLL_FIRST_MS
    let waited = 0
    const tick = () => {
      timer = setTimeout(() => {
        waited += gap
        latest.current()
        if (waited >= POLL_GIVE_UP_MS) return
        gap = Math.min(Math.round(gap * 1.5), POLL_MAX_MS)
        tick()
      }, gap)
    }
    tick()
    return () => { if (timer) clearTimeout(timer) }
  }, [processing, signature])
}
